import { useSearchParams } from "react-router-dom";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TABS = [
  { id: "faq", label: "FAQ" },
  { id: "shipping", label: "Shipping & Returns" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "terms", label: "Terms of Service" },
];

const FAQ = [
  ["When does Drop 001 go live?", "Drop 001 — The Founders Drop — is open now for Noir List members in limited quantities. Public access follows. Join the Noir List for exact windows."],
  ["How do Jae Noir tees fit?", "Our tees and hoodies are cut oversized and boxy with drop shoulders. Take your true size for the intended silhouette, or size down for a standard fit. Every product page carries a full size guide."],
  ["What does the GSM number mean?", "GSM is fabric weight — grams per square meter. Our tees run 220–260 GSM and our fleece 420–450 GSM, far above standard blanks. Heavier fabric, longer life, better drape."],
  ["Are limited pieces restocked?", "Numbered limited drops are never repressed in the same run. Core pieces may restock; restocks are announced to the Noir List first."],
  ["Do you ship internationally?", "Yes. Shipping rates and times are calculated per destination once checkout opens. US shipping is free over $150."],
];

const POLICY_SECTIONS = {
  shipping: [
    ["Shipping", "Orders ship within 3–5 business days of a drop closing. Limited pieces are made in capped runs and ship with numbered packaging. US orders over $150 ship free; international rates are calculated at checkout once Drop 001 checkout opens."],
    ["Returns", "Core pieces can be returned unworn within 14 days of delivery. Limited Drop and numbered pieces are final sale — they are produced in exact quantities and cannot be restocked."],
    ["Exchanges", "Size exchanges on core pieces are honored while stock lasts. Contact support@jaenoir.com with your order number."],
  ],
  privacy: [
    ["What we collect", "Your email when you join the Noir List, and your name, email, and order details when you create an account or purchase. Nothing more."],
    ["How we use it", "Drop announcements, order updates, and member access. We never sell your data. Emails are limited to what matters — releases and restocks."],
    ["Your control", "Unsubscribe from the Noir List at any time via any email footer. Request account deletion at support@jaenoir.com."],
  ],
  terms: [
    ["The short version", "Jae Noir garments are produced in limited runs. Product images are representative; hand-washed pieces vary slightly — that is the point."],
    ["Purchases", "All sales of limited and numbered pieces are final. Prices are in USD. We reserve the right to cancel orders that violate purchase limits on limited drops."],
    ["Intellectual property", "The JN monogram, crown crest, and all Jae Noir artwork are the property of Jae Noir. Don't copy the work — build your own legacy."],
  ],
};

export default function Info() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "faq";

  return (
    <div className="pt-16" data-testid="info-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-24 sm:pb-32">
        <Reveal><Eyebrow>Support</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl mt-4 leading-[0.9]">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="flex flex-wrap gap-1 mt-10 border border-white/10 p-1 w-fit" data-testid="info-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setParams({ tab: t.id })}
                className={`px-4 py-2.5 font-mono2 text-[9px] sm:text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${tab === t.id ? "bg-gold text-noir-bg" : "text-cream/50 hover:text-gold"}`}
                data-testid={`info-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-12">
            {tab === "faq" ? (
              <Accordion type="single" collapsible className="border-t border-white/10" data-testid="faq-accordion">
                {FAQ.map(([q, a], i) => (
                  <AccordionItem key={i} value={`q${i}`} className="border-white/10">
                    <AccordionTrigger className="font-display text-sm sm:text-base font-semibold uppercase tracking-wide text-left hover:text-gold hover:no-underline" data-testid={`faq-question-${i}`}>{q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-cream/50 leading-relaxed">{a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="space-y-10" data-testid={`info-${tab}-content`}>
                {POLICY_SECTIONS[tab].map(([title, body], i) => (
                  <div key={i} className="border-l-2 border-gold/40 pl-6">
                    <h3 className="font-display text-lg font-bold uppercase tracking-wide">{title}</h3>
                    <p className="mt-3 text-sm text-cream/50 leading-relaxed max-w-2xl">{body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
